import {
  type BatchChangeCommunicationsMutationVariables,
  type DownloadCommunicationsMutationVariables,
  type BatchChangeIdVersion,
  type Communication,
  type CreateCommunicationInput,
  type CreateCommunicationMutationVariables,
  type DeleteEntityByIdMutationVariables,
  type LooseObject,
  type MutationStatus,
  type OnCreateCommunicationSubscription,
  type OnCreateSignatureDocumentSubscription,
  type OnDeleteCommunicationSubscription,
  type OnDeleteSignatureDocumentSubscription,
  type OnUpdateCommunicationSubscription,
  type OnUpdateSignatureDocumentSubscription,
  type SignatureDocument,
  type Statement,
  type Unit,
  type DownloadCommunicationsResult,
  type SubmitCommunicationDialogInput,
  type SubmitCommunicationDialogMutationVariables,
  type SubmitCommunicationDialogResult,
  type DateTime,
  BatchChangeCommunicationsAction,
  COMMUNICATION_BULK_CONCURRENCY,
  COMMUNICATION_TABLE_NAME,
  CommunicationStatus,
  CommunicationType,
  DeepDeletableEntityType,
  processInChunks,
  SignatureDocumentStatus,
  splitStatementCommunications,
  type StatementCommunications,
  batchChangeCommunications as batchChangeCommunicationsMutation,
  buildCommunicationCopyInput,
  cleanInputCreate,
  cleanInputUpdate,
  createCommunication as createMutation,
  deleteEntityById as deleteEntityByIdMutation,
  getCommunication as getQuery,
  getReadId,
  getTableClientId,
  isHistoryCommunicationStatus,
  isLetterChannel,
  isNilOrEmpty,
  onCreateCommunication,
  onDeleteCommunication,
  onUpdateCommunication,
  onUpdateSignatureDocument,
  updateCommunication as updateMutation,
  downloadCommunications as downloadCommunicationsMutation,
  submitCommunicationDialog as submitCommunicationDialogMutation,
  fromDateTime,
  getDateTimeDifference,
  todayDateTime,
  calendarDateTimeFromTimestamp,
  isInstantBefore,
} from '@rentguru/commons-utils';
import {
  isTooEarlyToRetryFailedFetch,
  MILLISECONDS_BEFORE_RETRYING_A_FAILED_FETCH,
  NUMBER_OF_MINUTES_FOR_REFETCH,
  NUMBER_OF_MINUTES_FOR_RESYNC,
  get,
  list,
  mutation,
  recordWasUpdated,
  useSubscriptions,
} from '@up2rent/graphql';
import { toast } from '@up2rent/ui-new';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import uniqBy from 'lodash/uniqBy';
import xor from 'lodash/xor';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
  type Reducer,
} from 'react';
import { useIntl } from 'react-intl';

import {
  buildCommunicationDownloadFileNameBase,
  handleDownloadResult,
} from '../components/Communications/downloadCommunicationUtils';
import { useLocale } from '../i18n/IntlProviderWrapper';
import { getCommunicationBody, syncCommunicationBodies, syncLightCommunications } from '../tenantHooks/graphqlHelper';
import {
  type CommunicationBodiesById,
  type CommunicationBodySource,
  getCommunicationBodySourceKey,
  getOwnerEntityIds,
  isNewerVersion,
  mergeCommunicationBodies,
  withEmptyBodiesForMissing,
} from '../utils/communicationBodyUtils';
import { CommunicationDisplayStatus, getCommunicationDisplayStatus } from '../utils/communicationUtils';

import { useContextLoader } from './ContextLoader';
import { useSignatureDocuments } from './SignatureDocumentContext';
import { useUser } from './UserContext';
import { emptyContextLoader } from './utils/contextLoaderUtils';
import { usePermissions } from './utils/PermissionsContext';

export enum CommunicationCategory {
  TO_SEND,
  HISTORY,
  OUTBOX,
  OUTDATED,
}

export enum FetchByType {
  BY_LEASE = 'leaseId',
  BY_CLIENT = 'clientId',
  BY_BUILDING = 'buildingId',
  BY_CONTACT = 'contactId',
  BY_INVOICE = 'invoiceId',
}

export interface ClassifiedCommunications {
  communicationsToSend: Communication[];
  communicationsOutbox: Communication[];
  communicationsHistory: Communication[];
  communicationsOutdated: Communication[];
}

const LIMIT_LIGHT_COMMUNICATIONS = 1000;
const MAX_COMMUNICATION_BODIES_FETCHED_BY_ID = 10;

export interface CommunicationContext extends CommunicationState, ClassifiedCommunications {
  communicationsToSendSettled: boolean;
  createCommunication: (input: Omit<Communication, 'id' | 'clientId' | 'readId'>) => Promise<Communication | undefined>;
  updateCommunication: (
    original: Communication | { id: string },
    updates: Partial<Communication>
  ) => Promise<Communication | undefined>;
  deleteCommunication: (id: string) => Promise<MutationStatus | undefined>;
  batchDeleteCommunications: (communicationsIdsVersions: BatchChangeIdVersion[]) => Promise<boolean>;
  batchSendCommunications: (communicationsIdsVersions: BatchChangeIdVersion[], resend?: boolean) => Promise<boolean>;
  sendOrScheduleCommunications: (communications: Communication[], scheduledAt: DateTime | null) => Promise<number>;
  ignoreCommunications: (communications: Communication[]) => Promise<number>;
  markCommunicationsAsSent: (communications: Communication[], sentAt: DateTime) => Promise<number>;
  getCommunicationsForBuilding: (buildingId: string) => Communication[];
  getCommunicationsForUnit: (unitId: string) => Communication[];
  getLoadedCommunicationsForUnit: (unitId: string) => Communication[];
  requestCommunicationsForUnit: (unitId: string) => void;
  getCommunicationsForLease: (leaseId: string) => Communication[];
  getLoadedCommunicationsForLease: (leaseId: string) => Communication[];
  hasLoadedCommunicationsForLease: (leaseId: string) => boolean;
  requestCommunicationsForLease: (leaseId: string) => void;
  getLightCommunicationsForCategory: (communicationCategory: CommunicationCategory) => Communication[];
  requestLightCommunicationsForCategory: (communicationCategory: CommunicationCategory, force?: boolean) => void;
  getLightCommunicationsForOwner: (ownerUnits: Unit[], ownerId: string) => Communication[];
  getCommunication: (id: string) => Communication | undefined;
  getLoadedCommunication: (id: string) => Communication | undefined;
  requestCommunication: (id: string) => void;
  communicationBodiesById: CommunicationBodiesById;
  bodyFetchRetryCount: number;
  ensureCommunicationBodies: (missingIds: string[], source?: CommunicationBodySource) => Promise<void>;
  setSyncCommunications: (force?: boolean) => void;
  downloadCommunications: (communicationIds: string[]) => Promise<DownloadCommunicationsResult | null>;
  downloadCommunicationFiles: (communications: Communication[]) => Promise<boolean>;
  generateAndDownloadCommunication: (communicationId: string) => Promise<void>;
  isDownloadingCommunication: (communicationId: string) => boolean;
  submitCommunicationDialog: (
    payload: Omit<SubmitCommunicationDialogInput, 'clientId'>
  ) => Promise<SubmitCommunicationDialogResult | null>;
}

export interface CommunicationState {
  communicationsRaw: Communication[];
  communicationsToSendOutboxRaw: Communication[];
  communicationsHistoryRaw: Communication[];
  communicationsOutdatedRaw: Communication[];
  communicationsLoading?: boolean;
  communicationsError?: string;
  lastSync?: DateTime;
  lastHistorySync?: DateTime;
  lastOutdatedSync?: DateTime;
  lastSyncFailure?: DateTime;
  shouldSyncLight: boolean;
  shouldFetchCommunications: string[];
  shouldFetchToSendOutboxCommunications: boolean;
  shouldFetchHistoryCommunications: boolean;
  shouldFetchOutdatedCommunications: boolean;
  shouldFetchByBuilding: string[];
  shouldFetchByLease: string[];
  shouldFetchByUnit: string[];
  shouldFetchByOwner: { leaseIds: string[]; unitIds: string[]; buildingIds: string[]; ownerId: string }[];
  lastCommunicationIdFetch?: string;
  lastCommunicationsFetch: { [communicationId: string]: DateTime | null };
}

type CommunicationFetchQueue = 'communication' | 'building' | 'lease' | 'unit' | 'owner';

interface FailedCommunicationFetch {
  queue: CommunicationFetchQueue;
  id: string;
}

type Action =
  | {
      type: 'SHOULD_SYNC_LIGHT' | 'IS_FETCHING';
      payload?: { force: boolean };
    }
  | {
      type: 'ADD_COMMUNICATION';
      payload: { communication: Communication };
    }
  | {
      type: 'SHOULD_FETCH';
      payload: {
        communicationCategory: CommunicationCategory;
        force?: boolean;
      };
    }
  | {
      type: 'FETCHED';
      payload: {
        communications: Communication[];
        communicationCategory?: CommunicationCategory;
        error?: string;
        shouldUpdateLastSync?: boolean;
        fetchedCommunicationId?: string;
      };
    }
  | {
      type: 'ERROR';
      payload: { error: string };
    }
  | {
      type: 'FETCH_FAILED';
      payload?: FailedCommunicationFetch;
    }
  | {
      type: 'FAILURE_BACKOFF_ELAPSED';
    }
  | {
      type: 'UPDATE_COMMUNICATION';
      payload: { communication: Communication };
    }
  | {
      type: 'UPDATE_SIGNATURE_DOCUMENT_IN_COMMUNICATION';
      payload: { signatureDocument: SignatureDocument };
    }
  | {
      type: 'DELETE_COMMUNICATION' | 'FETCH_CLASSIC' | 'COMMUNICATION_NOT_FOUND';
      payload: { id: string };
    }
  | {
      type: 'DELETE_COMMUNICATIONS';
      payload: { ids: string[] };
    }
  | {
      type: 'FETCH_BUILDING';
      payload: { buildingId: string };
    }
  | {
      type: 'FETCH_LEASE';
      payload: { leaseId: string };
    }
  | {
      type: 'FETCH_UNIT';
      payload: { unitId: string };
    }
  | {
      type: 'FETCH_OWNER';
      payload: { ownerLeaseIds: string[]; ownerUnitIds: string[]; ownerBuildingIds: string[]; ownerId: string };
    };

export const initialState: CommunicationState = {
  communicationsRaw: [],
  communicationsToSendOutboxRaw: [],
  communicationsHistoryRaw: [],
  communicationsOutdatedRaw: [],
  communicationsLoading: false,
  communicationsError: undefined,
  shouldSyncLight: false,
  shouldFetchCommunications: [],
  shouldFetchToSendOutboxCommunications: false,
  shouldFetchHistoryCommunications: false,
  shouldFetchOutdatedCommunications: false,
  shouldFetchByBuilding: [],
  shouldFetchByLease: [],
  shouldFetchByUnit: [],
  shouldFetchByOwner: [],
  lastCommunicationsFetch: {},
};

interface CommunicationEntityLookups {
  leaseIds: Set<string>;
  unitIds: Set<string>;
  signatureDocumentsById: Map<string, SignatureDocument>;
}

const getFilteredAndClassifiedCommunicationsWithEntity = (
  communications: Communication[],
  entityLookups: CommunicationEntityLookups,
  signatureDocuments: SignatureDocument[] | null
): ClassifiedCommunications => {
  const { leaseIds, unitIds, signatureDocumentsById } = entityLookups;
  const filteredCommunications = communications.filter((communication) => {
    if (communication.leaseId) {
      return leaseIds.has(communication.leaseId);
    }
    if (communication.unitId) {
      return unitIds.has(communication.unitId);
    }

    return (
      !isEmpty(communication.contactIds) ||
      !isNil(communication.contactId) ||
      !isNil(communication.buildingId) ||
      communication.type === CommunicationType.CUSTOM
    );
  });
  const communicationsWithEntities = filteredCommunications.map((communication) => {
    const { signatureDocumentId, signatureDocument } = communication;
    if (isNil(signatureDocumentId) || !isNil(signatureDocument)) {
      return communication;
    }
    return { ...communication, signatureDocument: signatureDocumentsById.get(signatureDocumentId) };
  });

  return classifyCommunications(communicationsWithEntities, signatureDocuments ?? []);
};

const addSignatureDocumentToMatchingCommunication = (
  communications: Communication[],
  signatureDocument: SignatureDocument
) =>
  communications.map((communication) => {
    if (communication.signatureDocumentId === signatureDocument.id) {
      communication.signatureDocument = signatureDocument;
    }
    return communication;
  });

const addCommunicationToRawsByStatus = (state: CommunicationState, communication: Communication) => {
  const belongsToHistory = isHistoryCommunicationStatus(communication.status);
  const isOutdated = communication.status === CommunicationStatus.OUTDATED;
  return {
    communicationsRaw: [communication, ...state.communicationsRaw],
    communicationsHistoryRaw: belongsToHistory
      ? [communication, ...state.communicationsHistoryRaw]
      : state.communicationsHistoryRaw,
    communicationsOutdatedRaw: isOutdated
      ? [communication, ...state.communicationsOutdatedRaw]
      : state.communicationsOutdatedRaw,
    communicationsToSendOutboxRaw:
      belongsToHistory || isOutdated
        ? state.communicationsToSendOutboxRaw
        : [communication, ...state.communicationsToSendOutboxRaw],
  };
};

const removeCommunicationsFromRaws = (state: CommunicationState, idsToRemove: Set<string>): CommunicationState => ({
  ...state,
  communicationsRaw: state.communicationsRaw.filter((communication) => !idsToRemove.has(communication.id)),
  communicationsToSendOutboxRaw: state.communicationsToSendOutboxRaw.filter(
    (communication) => !idsToRemove.has(communication.id)
  ),
  communicationsHistoryRaw: state.communicationsHistoryRaw.filter(
    (communication) => !idsToRemove.has(communication.id)
  ),
  communicationsOutdatedRaw: state.communicationsOutdatedRaw.filter(
    (communication) => !idsToRemove.has(communication.id)
  ),
});

interface ClearedShouldFetchFlags {
  shouldFetchToSendOutboxCommunications?: boolean;
  shouldFetchHistoryCommunications?: boolean;
  shouldFetchOutdatedCommunications?: boolean;
}

const getClearedShouldFetchForCategory = (communicationCategory?: CommunicationCategory): ClearedShouldFetchFlags => {
  switch (communicationCategory) {
    case CommunicationCategory.TO_SEND:
    case CommunicationCategory.OUTBOX:
      return { shouldFetchToSendOutboxCommunications: false };
    case CommunicationCategory.HISTORY:
      return { shouldFetchHistoryCommunications: false };
    case CommunicationCategory.OUTDATED:
      return { shouldFetchOutdatedCommunications: false };
    default:
      return {
        shouldFetchToSendOutboxCommunications: false,
        shouldFetchHistoryCommunications: false,
        shouldFetchOutdatedCommunications: false,
      };
  }
};

const isTooEarlyToRetryAfterFailure = (state: CommunicationState): boolean =>
  isTooEarlyToRetryFailedFetch(state.lastSyncFailure);

const isWithinResyncDelay = (lastSync?: DateTime): boolean =>
  !isNil(lastSync) &&
  getDateTimeDifference({ start: lastSync, end: todayDateTime() }, 'minutes') < NUMBER_OF_MINUTES_FOR_RESYNC;

const getQueuesWithoutFailedFetch = (
  state: CommunicationState,
  failedFetch?: FailedCommunicationFetch
): Partial<CommunicationState> => {
  if (isNil(failedFetch)) {
    return {};
  }
  const { queue, id } = failedFetch;
  switch (queue) {
    case 'communication':
      return { shouldFetchCommunications: state.shouldFetchCommunications.filter((queuedId) => queuedId !== id) };
    case 'building':
      return { shouldFetchByBuilding: state.shouldFetchByBuilding.filter((queuedId) => queuedId !== id) };
    case 'lease':
      return { shouldFetchByLease: state.shouldFetchByLease.filter((queuedId) => queuedId !== id) };
    case 'unit':
      return { shouldFetchByUnit: state.shouldFetchByUnit.filter((queuedId) => queuedId !== id) };
    case 'owner':
      return { shouldFetchByOwner: state.shouldFetchByOwner.filter((queued) => queued.ownerId !== id) };
    default:
      return {};
  }
};

export const communicationReducer = (state: CommunicationState, action: Action): CommunicationState => {
  switch (action.type) {
    case 'SHOULD_SYNC_LIGHT':
      const shouldHoldLightSync =
        !action.payload?.force && (isTooEarlyToRetryAfterFailure(state) || isWithinResyncDelay(state.lastSync));
      if (state.communicationsLoading || shouldHoldLightSync) {
        return state;
      }
      return {
        ...state,
        shouldSyncLight: true,
      };
    case 'SHOULD_FETCH':
      const communicationCategoryFromAction = action.payload.communicationCategory;
      const forceFetch = action.payload.force ?? false;
      if (isNil(communicationCategoryFromAction) || (!forceFetch && isTooEarlyToRetryAfterFailure(state))) {
        return state;
      }
      if ([CommunicationCategory.OUTBOX, CommunicationCategory.TO_SEND].includes(communicationCategoryFromAction)) {
        const isToSendSyncPending = state.shouldFetchToSendOutboxCommunications || state.shouldSyncLight;
        const isToSendSyncRecent = !forceFetch && isWithinResyncDelay(state.lastSync);
        if (isToSendSyncPending || isToSendSyncRecent) {
          return state;
        }
        return { ...state, shouldFetchToSendOutboxCommunications: true };
      }
      if (communicationCategoryFromAction === CommunicationCategory.HISTORY) {
        const isHistorySyncRecent = !forceFetch && isWithinResyncDelay(state.lastHistorySync);
        if (state.shouldFetchHistoryCommunications || isHistorySyncRecent) {
          return state;
        }
        return { ...state, shouldFetchHistoryCommunications: true };
      }
      if (communicationCategoryFromAction === CommunicationCategory.OUTDATED) {
        const isOutdatedSyncRecent = !forceFetch && isWithinResyncDelay(state.lastOutdatedSync);
        if (state.shouldFetchOutdatedCommunications || isOutdatedSyncRecent) {
          return state;
        }
        return { ...state, shouldFetchOutdatedCommunications: true };
      }
      return state;
    case 'IS_FETCHING':
      return {
        ...state,
        communicationsLoading: true,
      };
    case 'FETCH_FAILED':
      // The failed request leaves its queue so a later request can enqueue it again, after the
      // same backoff as a failed sync: the getters enqueue on every render.
      return {
        ...state,
        ...getQueuesWithoutFailedFetch(state, action.payload),
        communicationsLoading: false,
        lastSyncFailure: todayDateTime(),
      };
    case 'FAILURE_BACKOFF_ELAPSED':
      // Clearing the failure re-renders the consumers, whose getters enqueue the failed fetch again.
      return { ...state, lastSyncFailure: undefined };
    case 'FETCHED':
      if (action.payload.error) {
        return {
          ...state,
          ...getClearedShouldFetchForCategory(action.payload.communicationCategory),
          communicationsLoading: false,
          shouldSyncLight: false,
          lastSyncFailure: todayDateTime(),
          communicationsError: action.payload.error,
        };
      }

      const updatedState: {
        communicationsToSendOutboxRaw?: Communication[];
        communicationsHistoryRaw?: Communication[];
        communicationsOutdatedRaw?: Communication[];
        communicationsRaw?: Communication[];
        shouldFetchToSendOutboxCommunications?: boolean;
        shouldFetchHistoryCommunications?: boolean;
        shouldFetchOutdatedCommunications?: boolean;
        lastSync?: DateTime;
        lastHistorySync?: DateTime;
        lastOutdatedSync?: DateTime;
        lastCommunicationsFetch: { [communicationId: string]: DateTime | null };
      } = { ...state, lastCommunicationsFetch: { ...state.lastCommunicationsFetch } };

      const actionCommunicationCategory = action.payload.communicationCategory;
      // Only a category sync can clear the category error: a single-communication or entity fetch
      // succeeding says nothing about the category that failed.
      const isFetchedCategorySync = !isNil(actionCommunicationCategory);
      if (isNil(actionCommunicationCategory)) {
        const filteredIds = action.payload.communications.map((communication) => communication.id);
        const newCommunicationsRaw = action.payload.communications.reduce((accumulator, newCommunication) => {
          const oldCommunication = updatedState.communicationsRaw?.find(
            (communication) => communication.id === newCommunication.id
          );
          if (!isNil(oldCommunication) && !isNilOrEmpty(oldCommunication.body) && isNilOrEmpty(newCommunication.body)) {
            if (!isNewerVersion(newCommunication._version, oldCommunication._version)) {
              accumulator.push(oldCommunication);
              return accumulator;
            }
            delete updatedState.lastCommunicationsFetch[newCommunication.id];
          }
          accumulator.push(newCommunication);

          return accumulator;
        }, [] as Communication[]);

        updatedState.communicationsRaw = [
          ...(updatedState.communicationsRaw ?? []).filter((communication) => !filteredIds.includes(communication.id)),
          ...newCommunicationsRaw,
        ];
        const now = todayDateTime();
        if (action.payload.shouldUpdateLastSync) {
          action.payload.communications.forEach((communication) => {
            const isFullCommunication = !isNilOrEmpty(communication.body);
            if (isFullCommunication || communication.id === action.payload.fetchedCommunicationId) {
              updatedState.lastCommunicationsFetch[communication.id] = now;
            }
          });
        }
      } else if ([CommunicationCategory.TO_SEND, CommunicationCategory.OUTBOX].includes(actionCommunicationCategory)) {
        updatedState.communicationsToSendOutboxRaw = action.payload.communications;
        updatedState.shouldFetchToSendOutboxCommunications = false;
        updatedState.lastSync = todayDateTime();
      } else if (actionCommunicationCategory === CommunicationCategory.HISTORY) {
        updatedState.communicationsHistoryRaw = action.payload.communications;
        updatedState.shouldFetchHistoryCommunications = false;
        updatedState.lastHistorySync = todayDateTime();
      } else if (actionCommunicationCategory === CommunicationCategory.OUTDATED) {
        updatedState.communicationsOutdatedRaw = action.payload.communications;
        updatedState.shouldFetchOutdatedCommunications = false;
        updatedState.lastOutdatedSync = todayDateTime();
      }

      const fullCommunicationIds = isFetchedCategorySync
        ? new Set<string>()
        : action.payload.communications.reduce((acc, fetchedCommunication) => {
            if (!isNilOrEmpty(fetchedCommunication.body)) {
              acc.add(fetchedCommunication.id);
            }
            return acc;
          }, new Set<string>());
      const shouldFetchCommunications = state.shouldFetchCommunications.filter(
        (communicationId) =>
          communicationId !== action.payload.fetchedCommunicationId && !fullCommunicationIds.has(communicationId)
      );

      return {
        ...state,
        ...updatedState,
        shouldFetchCommunications,
        communicationsLoading: false,
        shouldSyncLight: false,
        ...(isFetchedCategorySync && { lastSyncFailure: undefined, communicationsError: undefined }),
      };

    case 'FETCH_BUILDING':
      const actionBuildingId = action.payload.buildingId;
      const currentShouldFetchByBuilding = state.shouldFetchByBuilding;
      if (currentShouldFetchByBuilding.includes(actionBuildingId) || isTooEarlyToRetryAfterFailure(state)) {
        return state;
      }

      return { ...state, shouldFetchByBuilding: [actionBuildingId, ...currentShouldFetchByBuilding] };
    case 'FETCH_LEASE':
      const actionLeaseId = action.payload.leaseId;
      const currentShouldFetchByLease = state.shouldFetchByLease;
      if (currentShouldFetchByLease.includes(actionLeaseId) || isTooEarlyToRetryAfterFailure(state)) {
        return state;
      }

      return { ...state, shouldFetchByLease: [actionLeaseId, ...currentShouldFetchByLease] };

    case 'FETCH_UNIT':
      const actionUnitId = action.payload.unitId;
      const currentShouldFetchByUnit = state.shouldFetchByUnit;
      if (currentShouldFetchByUnit.includes(actionUnitId) || isTooEarlyToRetryAfterFailure(state)) {
        return state;
      }

      return { ...state, shouldFetchByUnit: [actionUnitId, ...currentShouldFetchByUnit] };
    case 'FETCH_OWNER':
      const actionLeaseIds = action.payload.ownerLeaseIds;
      const actionUnitIds = action.payload.ownerUnitIds;
      const actionBuildingIds = action.payload.ownerBuildingIds;
      const actionOwnerId = action.payload.ownerId;
      if (
        isEmpty(actionLeaseIds) &&
        isEmpty(actionUnitIds) &&
        isEmpty(actionBuildingIds) &&
        isNilOrEmpty(actionOwnerId)
      ) {
        return state;
      }
      const shouldFetchByOwnerObject = {
        leaseIds: actionLeaseIds,
        unitIds: actionUnitIds,
        buildingIds: actionBuildingIds,
        ownerId: actionOwnerId,
      };
      const currentShouldFetchByOwner = state.shouldFetchByOwner;
      if (
        currentShouldFetchByOwner.some((object) => object.ownerId === actionOwnerId) ||
        isTooEarlyToRetryAfterFailure(state)
      ) {
        return state;
      }
      return { ...state, shouldFetchByOwner: [shouldFetchByOwnerObject, ...currentShouldFetchByOwner] };
    case 'FETCH_CLASSIC':
      const actionCommunicationId = action.payload.id;
      const lastCommunicationIdFetch = state.lastCommunicationsFetch[actionCommunicationId];
      if (
        isNilOrEmpty(actionCommunicationId) ||
        state.shouldFetchCommunications.includes(actionCommunicationId) ||
        isTooEarlyToRetryAfterFailure(state) ||
        (lastCommunicationIdFetch &&
          getDateTimeDifference({ start: lastCommunicationIdFetch, end: todayDateTime() }, 'minutes') <
            NUMBER_OF_MINUTES_FOR_REFETCH)
      ) {
        return state;
      }

      return { ...state, shouldFetchCommunications: [actionCommunicationId, ...state.shouldFetchCommunications] };
    case 'ADD_COMMUNICATION':
      const communicationCreated = action.payload.communication;
      return {
        ...state,
        ...addCommunicationToRawsByStatus(state, communicationCreated),
      };
    case 'UPDATE_COMMUNICATION':
      const updatedCommunication = action.payload.communication;
      const oldRecord =
        state.communicationsRaw.find((communication) => communication.id === updatedCommunication.id) ??
        state.communicationsToSendOutboxRaw.find((communication) => communication.id === updatedCommunication.id);
      if (isNil(oldRecord)) {
        return { ...state, ...addCommunicationToRawsByStatus(state, updatedCommunication) };
      }
      if (!recordWasUpdated(oldRecord, updatedCommunication)) {
        return state;
      }
      const updatedCommunicationHasError = updatedCommunication.status === CommunicationStatus.ERROR;
      const updatedCommunicationMovedToHistory = isHistoryCommunicationStatus(updatedCommunication.status);
      const toSendCommunicationIsOutdated =
        updatedCommunication.status === CommunicationStatus.OUTDATED &&
        oldRecord.status === CommunicationStatus.TO_SEND;
      const outdatedCommunicationIsToSend =
        oldRecord.status === CommunicationStatus.OUTDATED &&
        updatedCommunication.status === CommunicationStatus.TO_SEND;
      const outdatedCommunicationIsSent =
        oldRecord.status === CommunicationStatus.OUTDATED && updatedCommunication.status === CommunicationStatus.SENT;

      const newCommunicationsRaw = state.communicationsRaw.reduce(
        (accumulator: Communication[], communication: Communication) => {
          if (communication.id === updatedCommunication.id) {
            if (toSendCommunicationIsOutdated) {
              return accumulator;
            }
            accumulator.push(updatedCommunication);
            return accumulator;
          }
          accumulator.push(communication);
          return accumulator;
        },
        []
      );

      const newCommunicationsHistoryRaw = updatedCommunicationHasError
        ? state.communicationsHistoryRaw.filter((communication) => communication.id !== updatedCommunication.id)
        : [...state.communicationsHistoryRaw];

      if (updatedCommunicationMovedToHistory) {
        newCommunicationsHistoryRaw.unshift(updatedCommunication);
      }

      const newCommunicationsToSendOutboxRaw = state.communicationsToSendOutboxRaw.reduce(
        (accumulator: Communication[], communication: Communication) => {
          if (communication.id === updatedCommunication.id) {
            if (updatedCommunicationMovedToHistory || updatedCommunicationHasError) {
              return accumulator;
            }
            accumulator.push(updatedCommunication);
            return accumulator;
          }
          accumulator.push(communication);
          return accumulator;
        },
        []
      );

      if (updatedCommunicationHasError) {
        newCommunicationsToSendOutboxRaw.unshift(updatedCommunication);
      }

      const newCommunicationsOutdatedRaw = state.communicationsOutdatedRaw.reduce(
        (accumulator: Communication[], communication: Communication) => {
          if (communication.id === updatedCommunication.id) {
            if (outdatedCommunicationIsSent || outdatedCommunicationIsToSend) {
              return accumulator;
            }
            accumulator.push(updatedCommunication);
            return accumulator;
          }
          accumulator.push(communication);
          return accumulator;
        },
        []
      );

      if (toSendCommunicationIsOutdated) {
        newCommunicationsOutdatedRaw.unshift(updatedCommunication);
      }

      if (outdatedCommunicationIsSent) {
        newCommunicationsHistoryRaw.unshift(updatedCommunication);
      }

      if (outdatedCommunicationIsToSend) {
        newCommunicationsRaw.unshift(updatedCommunication);
      }

      return {
        ...state,
        communicationsRaw: uniqBy(newCommunicationsRaw, 'id'),
        communicationsHistoryRaw: uniqBy(newCommunicationsHistoryRaw, 'id'),
        communicationsToSendOutboxRaw: uniqBy(newCommunicationsToSendOutboxRaw, 'id'),
        communicationsOutdatedRaw: uniqBy(newCommunicationsOutdatedRaw, 'id'),
      };
    case 'UPDATE_SIGNATURE_DOCUMENT_IN_COMMUNICATION':
      const updatedSignatureDocument = action.payload.signatureDocument;
      return {
        ...state,
        communicationsRaw: addSignatureDocumentToMatchingCommunication(
          state.communicationsRaw,
          updatedSignatureDocument
        ),
        communicationsHistoryRaw: addSignatureDocumentToMatchingCommunication(
          state.communicationsHistoryRaw,
          updatedSignatureDocument
        ),
        communicationsToSendOutboxRaw: addSignatureDocumentToMatchingCommunication(
          state.communicationsToSendOutboxRaw,
          updatedSignatureDocument
        ),
        communicationsOutdatedRaw: addSignatureDocumentToMatchingCommunication(
          state.communicationsOutdatedRaw,
          updatedSignatureDocument
        ),
      };
    case 'DELETE_COMMUNICATION':
      return removeCommunicationsFromRaws(state, new Set([action.payload.id]));
    case 'COMMUNICATION_NOT_FOUND':
      return {
        ...removeCommunicationsFromRaws(state, new Set([action.payload.id])),
        communicationsLoading: false,
        shouldFetchCommunications: state.shouldFetchCommunications.filter((queuedId) => queuedId !== action.payload.id),
        lastCommunicationsFetch: { ...state.lastCommunicationsFetch, [action.payload.id]: todayDateTime() },
      };
    case 'DELETE_COMMUNICATIONS':
      return removeCommunicationsFromRaws(state, new Set(action.payload.ids));
    default:
      return state;
  }
};

export const classifyCommunications = (
  communications: Communication[],
  signatureDocuments?: SignatureDocument[] | null
): ClassifiedCommunications => {
  const classifiedCommunications = communications.reduce(
    (accumulator: ClassifiedCommunications, communication: Communication) => {
      if (isHistoryCommunicationStatus(communication.status)) {
        accumulator.communicationsHistory.push(communication);
        return accumulator;
      }
      if (communication.status === CommunicationStatus.ERROR) {
        accumulator.communicationsToSend.push(communication);
        return accumulator;
      }
      const isLetter = isLetterChannel(communication.channel);

      if (isLetter) {
        const relatedSignatureDocument = signatureDocuments?.find(
          (signatureDocument) => signatureDocument.id === communication.signatureDocumentId
        );
        if (communication.status === CommunicationStatus.OUTDATED) {
          accumulator.communicationsOutdated.push(communication);
          return accumulator;
        }
        const letterDisplayStatus = getCommunicationDisplayStatus(communication);
        if (
          relatedSignatureDocument?.status === SignatureDocumentStatus.SIGNED ||
          letterDisplayStatus === CommunicationDisplayStatus.LETTER_DELIVERED ||
          letterDisplayStatus === CommunicationDisplayStatus.LETTER_NOT_COLLECTED
        ) {
          accumulator.communicationsHistory.push(communication);
          return accumulator;
        }
        if (
          letterDisplayStatus === CommunicationDisplayStatus.SENDING ||
          letterDisplayStatus === CommunicationDisplayStatus.SCHEDULED ||
          letterDisplayStatus === CommunicationDisplayStatus.LETTER_IN_TRANSIT ||
          relatedSignatureDocument?.status === SignatureDocumentStatus.PENDING
        ) {
          accumulator.communicationsOutbox.push(communication);
          return accumulator;
        }
        accumulator.communicationsToSend.push(communication);
        return accumulator;
      }

      if (communication.status === CommunicationStatus.DRAFT) {
        accumulator.communicationsToSend.push(communication);
        return accumulator;
      }

      if (communication.status === CommunicationStatus.SCHEDULED) {
        accumulator.communicationsOutbox.push(communication);
        return accumulator;
      }
      if (communication.status === CommunicationStatus.SENDING) {
        accumulator.communicationsOutbox.push(communication);
        return accumulator;
      }
      if (communication.status === CommunicationStatus.TO_SEND && communication.toSend) {
        accumulator.communicationsOutbox.push(communication);
        return accumulator;
      }

      if (
        communication.status === CommunicationStatus.TO_SEND ||
        (communication.status === CommunicationStatus.SNOOZED &&
          !isNil(communication.snoozedDate) &&
          isInstantBefore(calendarDateTimeFromTimestamp(communication.snoozedDate), todayDateTime()))
      ) {
        accumulator.communicationsToSend.push(communication);
        return accumulator;
      }
      if (communication.status === CommunicationStatus.OUTDATED) {
        accumulator.communicationsOutdated.push(communication);
      }
      return accumulator;
    },
    {
      communicationsToSend: [],
      communicationsOutbox: [],
      communicationsHistory: [],
      communicationsOutdated: [],
    } as ClassifiedCommunications
  );
  return classifiedCommunications;
};

export const fetchCommunication = async (communicationId: string): Promise<Communication> => {
  const communicationResult = await get<Communication>(getQuery, communicationId);
  return communicationResult;
};

export const fetchLightCommunications = async (
  by: FetchByType,
  byValue: string,
  statusList?: CommunicationStatus[],
  query: string = syncLightCommunications
): Promise<Communication[]> => {
  const statusFilter: LooseObject | undefined = statusList
    ? { or: statusList.map((currentStatus) => ({ status: { eq: currentStatus } })) }
    : undefined;

  return await list<Communication>(query, by, byValue, statusFilter, undefined, LIMIT_LIGHT_COMMUNICATIONS);
};

/** Communications of a statement: changing what the statement attaches reaches the pending ones only. */
export const fetchStatementCommunications = async (
  statement: Pick<Statement, 'id' | 'leaseId' | 'ownerId'>
): Promise<StatementCommunications> => {
  const { id, leaseId, ownerId } = statement;
  const byValue = leaseId ?? ownerId;
  if (isNil(byValue)) {
    return { sent: [], pending: [] };
  }
  const communications = await list<Communication>(
    syncLightCommunications,
    isNil(leaseId) ? FetchByType.BY_CONTACT : FetchByType.BY_LEASE,
    byValue,
    { and: [{ statementId: { eq: id } }] },
    undefined,
    LIMIT_LIGHT_COMMUNICATIONS
  );
  return splitStatementCommunications(communications, id);
};

const createCommunicationRecord = async (input: Partial<Communication>, clientId: string): Promise<Communication> =>
  await mutation<Communication, CreateCommunicationMutationVariables>(createMutation, {
    input: {
      ...(cleanInputCreate(input) as CreateCommunicationInput),
      clientId: getTableClientId(clientId, COMMUNICATION_TABLE_NAME),
      readId: getReadId(clientId, COMMUNICATION_TABLE_NAME),
    },
  });

/** Creates a new communication from a sent one, waiting for a manual send. */
export const createCommunicationCopy = async (communicationId: string, clientId: string): Promise<Communication> =>
  await createCommunicationRecord(buildCommunicationCopyInput(await fetchCommunication(communicationId)), clientId);

const fetchLightCommunicationsByUnit = async (
  unitId: string,
  clientId: string,
  query: string = syncLightCommunications
): Promise<Communication[]> => {
  const additionalFilter = {
    and: [{ unitId: { eq: unitId } }],
  };

  return await list<Communication>(
    query,
    'clientId',
    clientId,
    additionalFilter,
    undefined,
    LIMIT_LIGHT_COMMUNICATIONS
  );
};

const fetchLightCommunicationsByBuilding = async (
  buildingId: string,
  buildingUnitIds: string[],
  clientId: string,
  query: string = syncLightCommunications
): Promise<Communication[]> => {
  const additionalFilter = {
    or: [{ buildingId: { eq: buildingId } }, ...buildingUnitIds.map((unitId) => ({ unitId: { eq: unitId } }))],
  };

  return await list<Communication>(
    query,
    'clientId',
    clientId,
    additionalFilter,
    undefined,
    LIMIT_LIGHT_COMMUNICATIONS
  );
};

const fetchLightCommunicationsForOwner = async (
  leaseIds: string[],
  unitIds: string[],
  buildingIds: string[],
  ownerId: string,
  clientId: string,
  query: string = syncLightCommunications
): Promise<Communication[]> => {
  const filters = [
    ...leaseIds.map((currentLeaseId) => ({ leaseId: { eq: currentLeaseId } })),
    ...unitIds.map((currentUnitId) => ({ unitId: { eq: currentUnitId } })),
    ...buildingIds.map((currentBuildingId) => ({ buildingId: { eq: currentBuildingId } })),
    { contactIds: { contains: ownerId } },
    { concernedContactIds: { contains: ownerId } },
  ];
  const additionalFilter = { or: filters };

  return await list<Communication>(
    query,
    'clientId',
    clientId,
    additionalFilter,
    undefined,
    LIMIT_LIGHT_COMMUNICATIONS
  );
};

const getCommunicationCategoryStatuses = (communicationCategory: CommunicationCategory): CommunicationStatus[] => {
  switch (communicationCategory) {
    case CommunicationCategory.TO_SEND:
      return [
        CommunicationStatus.TO_SEND,
        CommunicationStatus.SCHEDULED,
        CommunicationStatus.SENDING,
        CommunicationStatus.SNOOZED,
        CommunicationStatus.ERROR,
        CommunicationStatus.DRAFT,
      ];
    case CommunicationCategory.OUTBOX:
      return [
        CommunicationStatus.TO_SEND,
        CommunicationStatus.SCHEDULED,
        CommunicationStatus.SENDING,
        CommunicationStatus.SNOOZED,
      ];
    case CommunicationCategory.HISTORY:
      return [
        CommunicationStatus.SENT,
        CommunicationStatus.SENT_MANUALLY,
        CommunicationStatus.DOWNLOADED,
        CommunicationStatus.IGNORED,
      ];
    case CommunicationCategory.OUTDATED:
    default:
      return [CommunicationStatus.OUTDATED];
  }
};

// eslint-disable-next-line no-redeclare
export const CommunicationContext = createContext<CommunicationContext | null>(null);

export interface UnitsLoaderSnapshot {
  units: Unit[];
  isLoading: boolean;
}

const getUnitIdsOfBuilding = (units: Unit[], buildingId: string): string[] =>
  units.filter((unit) => unit.buildingId === buildingId).map((unit) => unit.id);

export const haveBuildingUnitsChangedSince = (
  startedWithUnitIds: string[],
  latestUnitsLoader: UnitsLoaderSnapshot,
  buildingId: string
): boolean =>
  latestUnitsLoader.isLoading ||
  !isEmpty(xor(startedWithUnitIds, getUnitIdsOfBuilding(latestUnitsLoader.units, buildingId)));

export const findNextEntityToFetch = <T,>(
  queue: T[],
  getEntityKey: (entry: T) => string,
  settledEntityKeys: ReadonlySet<string>[]
): T | undefined => queue.find((entry) => !settledEntityKeys.some((entityKeys) => entityKeys.has(getEntityKey(entry))));

const getBuildingFetchKey = (buildingId: string): string => `building:${buildingId}`;
const getLeaseFetchKey = (leaseId: string): string => `lease:${leaseId}`;
const getUnitFetchKey = (unitId: string): string => `unit:${unitId}`;
const getOwnerFetchKey = (ownerScope: CommunicationState['shouldFetchByOwner'][number]): string =>
  `owner:${ownerScope.ownerId}`;

interface CommunicationContextProviderProps {
  children?: ReactNode;
}

export const CommunicationContextProvider = ({ children }: CommunicationContextProviderProps) => {
  const [state, dispatch] = useReducer<Reducer<CommunicationState, Action>>(communicationReducer, initialState);
  const [downloadingCommunicationIds, setDownloadingCommunicationIds] = useState<string[]>([]);
  // The by-entity queues keep every requested id for the session; each effect fetches the newest
  // entry that is neither fetched nor in flight.
  const fetchedEntityKeys = useRef<Set<string>>(new Set());
  // A re-run must not drop the request in flight: the loading flag it raised blocks every later run.
  const inFlightEntityKeys = useRef<Set<string>>(new Set());
  const [communicationBodiesById, setCommunicationBodiesById] = useState<CommunicationBodiesById>({});
  const [bodyFetchRetryCount, setBodyFetchRetryCount] = useState<number>(0);
  const bodySourceLoads = useRef<Map<string, Promise<string[]>>>(new Map());
  const loadingBodyIds = useRef<Set<string>>(new Set());
  const { formatMessage } = useIntl();
  const { clientId, userId } = useUser();
  const { language } = useLocale();
  const { communicationsDetailsDelete } = usePermissions();
  const { signatureDocuments, loading: signatureDocumentsLoading } = useSignatureDocuments();
  const contextLoader = useContextLoader();
  const {
    Unit: { data: unitsLoader, loading: unitsLoading },
    Lease: { data: leasesLoader },
  } = contextLoader ?? emptyContextLoader;
  const contextLoaderLoading = contextLoader?.loading ?? false;

  const finishedLoading = !contextLoaderLoading && !unitsLoading && !signatureDocumentsLoading;

  // Read when a building fetch resolves: the effect's closure still holds the units it started with.
  const latestUnitsLoader = useRef<UnitsLoaderSnapshot>({
    units: unitsLoader,
    isLoading: unitsLoading,
  });
  useLayoutEffect(() => {
    latestUnitsLoader.current = { units: unitsLoader, isLoading: unitsLoading };
  }, [unitsLoader, unitsLoading]);

  const getBuildingUnitIds = (buildingId: string): string[] => getUnitIdsOfBuilding(unitsLoader, buildingId);

  const canFetchCommunications = !isNilOrEmpty(clientId);

  const getNextEntityToFetch = <T,>(queue: T[], getEntityKey: (entry: T) => string): T | undefined =>
    findNextEntityToFetch(queue, getEntityKey, [fetchedEntityKeys.current, inFlightEntityKeys.current]);

  const markEntityFetchInFlight = (entityKey: string) => {
    inFlightEntityKeys.current.add(entityKey);
  };

  const finishEntityFetch = (entityKey: string, succeeded: boolean) => {
    inFlightEntityKeys.current.delete(entityKey);
    if (succeeded) {
      fetchedEntityKeys.current.add(entityKey);
    }
  };

  useEffect(() => {
    if (isNil(state.lastSyncFailure)) {
      return undefined;
    }
    const retryTimeout = setTimeout(
      () => dispatch({ type: 'FAILURE_BACKOFF_ELAPSED' }),
      MILLISECONDS_BEFORE_RETRYING_A_FAILED_FETCH
    );
    return () => clearTimeout(retryTimeout);
  }, [state.lastSyncFailure]);

  useEffect(() => {
    if (state.shouldSyncLight && canFetchCommunications) {
      syncAndSetLightCommunications(CommunicationCategory.TO_SEND);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldSyncLight, canFetchCommunications]);

  useEffect(() => {
    if (state.shouldFetchToSendOutboxCommunications && canFetchCommunications) {
      syncAndSetLightCommunications(CommunicationCategory.TO_SEND);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchToSendOutboxCommunications, canFetchCommunications]);

  useEffect(() => {
    if (state.shouldFetchHistoryCommunications && canFetchCommunications) {
      syncAndSetLightCommunications(CommunicationCategory.HISTORY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchHistoryCommunications, canFetchCommunications]);

  useEffect(() => {
    if (state.shouldFetchOutdatedCommunications && canFetchCommunications) {
      syncAndSetLightCommunications(CommunicationCategory.OUTDATED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchOutdatedCommunications, canFetchCommunications]);

  const dispatchFetched = (communicationsToFetch: Communication[], shouldUpdateLastSync: boolean = true) => {
    dispatch({
      type: 'FETCHED',
      payload: {
        communications: communicationsToFetch,
        shouldUpdateLastSync,
      },
    });
  };

  useEffect(() => {
    let unmounted = false;
    const fetchAndSet = async () => {
      const communicationId = state.shouldFetchCommunications[0];
      if (!isEmpty(communicationId)) {
        dispatch({ type: 'IS_FETCHING' });
        try {
          const communication = await fetchCommunication(communicationId);
          if (unmounted) {
            return;
          }
          if (isNil(communication)) {
            dispatch({ type: 'COMMUNICATION_NOT_FOUND', payload: { id: communicationId } });
            return;
          }
          dispatch({
            type: 'FETCHED',
            payload: {
              communications: [communication],
              shouldUpdateLastSync: true,
              fetchedCommunicationId: communicationId,
            },
          });
        } catch (error) {
          console.error('[communications] fetching a communication failed', error);
          if (!unmounted) {
            dispatch({ type: 'FETCH_FAILED', payload: { queue: 'communication', id: communicationId } });
          }
        }
      }
    };
    if (!isEmpty(state.shouldFetchCommunications) && finishedLoading) {
      fetchAndSet();
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchCommunications, state.shouldFetchCommunications.length, finishedLoading]);

  useEffect(() => {
    const fetchAndSet = async () => {
      const buildingId = getNextEntityToFetch(state.shouldFetchByBuilding, getBuildingFetchKey);
      if (isNilOrEmpty(buildingId)) {
        return;
      }
      const entityKey = getBuildingFetchKey(buildingId);
      markEntityFetchInFlight(entityKey);
      dispatch({ type: 'IS_FETCHING' });
      const buildingUnitIds = getBuildingUnitIds(buildingId);
      try {
        const communicationsByBuilding = await fetchLightCommunicationsByBuilding(
          buildingId,
          buildingUnitIds,
          getTableClientId(clientId!, COMMUNICATION_TABLE_NAME)
        );
        finishEntityFetch(
          entityKey,
          !haveBuildingUnitsChangedSince(buildingUnitIds, latestUnitsLoader.current, buildingId)
        );
        dispatchFetched(communicationsByBuilding, false);
      } catch (error) {
        console.error('[communications] fetching the communications of a building failed', error);
        finishEntityFetch(entityKey, false);
        dispatch({ type: 'FETCH_FAILED', payload: { queue: 'building', id: buildingId } });
      }
    };
    if (!isEmpty(state.shouldFetchByBuilding) && !state.communicationsLoading && finishedLoading) {
      fetchAndSet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchByBuilding, state.communicationsLoading, finishedLoading]);

  useEffect(() => {
    const fetchAndSet = async () => {
      const leaseId = getNextEntityToFetch(state.shouldFetchByLease, getLeaseFetchKey);
      if (isNilOrEmpty(leaseId)) {
        return;
      }
      const entityKey = getLeaseFetchKey(leaseId);
      markEntityFetchInFlight(entityKey);
      dispatch({ type: 'IS_FETCHING' });
      try {
        const communicationsByLease = await fetchLightCommunications(FetchByType.BY_LEASE, leaseId);
        finishEntityFetch(entityKey, true);
        dispatchFetched(communicationsByLease, false);
      } catch (error) {
        console.error('[communications] fetching the communications of a lease failed', error);
        finishEntityFetch(entityKey, false);
        dispatch({ type: 'FETCH_FAILED', payload: { queue: 'lease', id: leaseId } });
      }
    };
    if (!isEmpty(state.shouldFetchByLease) && !state.communicationsLoading && finishedLoading) {
      fetchAndSet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchByLease, state.communicationsLoading, finishedLoading]);

  useEffect(() => {
    const fetchAndSet = async () => {
      const unitId = getNextEntityToFetch(state.shouldFetchByUnit, getUnitFetchKey);
      if (isNilOrEmpty(unitId)) {
        return;
      }
      const entityKey = getUnitFetchKey(unitId);
      markEntityFetchInFlight(entityKey);
      try {
        const communicationsByUnit = await fetchLightCommunicationsByUnit(
          unitId,
          getTableClientId(clientId!, COMMUNICATION_TABLE_NAME)
        );
        finishEntityFetch(entityKey, true);
        dispatchFetched(communicationsByUnit, false);
      } catch (error) {
        console.error('[communications] fetching the communications of a unit failed', error);
        finishEntityFetch(entityKey, false);
        dispatch({ type: 'FETCH_FAILED', payload: { queue: 'unit', id: unitId } });
      }
    };
    if (!isEmpty(state.shouldFetchByUnit) && !state.communicationsLoading && finishedLoading) {
      fetchAndSet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchByUnit, state.communicationsLoading, finishedLoading]);

  useEffect(() => {
    const fetchAndSet = async () => {
      const ownerLeaseAndUnitIds = getNextEntityToFetch(state.shouldFetchByOwner, getOwnerFetchKey);
      if (isNil(ownerLeaseAndUnitIds)) {
        return;
      }
      const hasOwnerScope =
        !isNil(ownerLeaseAndUnitIds.leaseIds) ||
        !isNil(ownerLeaseAndUnitIds.unitIds) ||
        !isNil(ownerLeaseAndUnitIds.buildingIds) ||
        !isNil(ownerLeaseAndUnitIds.ownerId);
      if (!hasOwnerScope) {
        return;
      }
      const entityKey = getOwnerFetchKey(ownerLeaseAndUnitIds);
      markEntityFetchInFlight(entityKey);
      dispatch({ type: 'IS_FETCHING' });
      try {
        const communicationsByOwner = await fetchLightCommunicationsForOwner(
          ownerLeaseAndUnitIds.leaseIds,
          ownerLeaseAndUnitIds.unitIds,
          ownerLeaseAndUnitIds.buildingIds,
          ownerLeaseAndUnitIds.ownerId,
          getTableClientId(clientId!, COMMUNICATION_TABLE_NAME)
        );
        finishEntityFetch(entityKey, true);
        dispatchFetched(communicationsByOwner, false);
      } catch (error) {
        console.error('[communications] fetching the communications of an owner failed', error);
        finishEntityFetch(entityKey, false);
        dispatch({ type: 'FETCH_FAILED', payload: { queue: 'owner', id: ownerLeaseAndUnitIds.ownerId } });
      }
    };
    if (!isEmpty(state.shouldFetchByOwner) && !state.communicationsLoading && finishedLoading) {
      fetchAndSet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchByOwner, state.communicationsLoading, finishedLoading]);

  const syncAndSetLightCommunications = async (communicationCategory: CommunicationCategory) => {
    try {
      dispatch({ type: 'IS_FETCHING' });
      const communications = await fetchLightCommunications(
        FetchByType.BY_CLIENT,
        getTableClientId(clientId!, COMMUNICATION_TABLE_NAME),
        getCommunicationCategoryStatuses(communicationCategory)
      );
      dispatch({ type: 'FETCHED', payload: { communications, communicationCategory } });
    } catch (error) {
      dispatch({
        type: 'FETCHED',
        payload: {
          communications: [],
          communicationCategory,
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }
  };

  useSubscriptions<
    OnCreateCommunicationSubscription,
    OnUpdateCommunicationSubscription,
    OnDeleteCommunicationSubscription
  >(
    onCreateCommunication,
    onUpdateCommunication,
    onDeleteCommunication,
    (data) => {
      const communicationCreated = data.onCreateCommunication as Communication;
      setCommunicationBodiesById((current) => mergeCommunicationBodies(current, [communicationCreated]));
      dispatch({
        type: 'ADD_COMMUNICATION',
        payload: { communication: communicationCreated },
      });
    },
    (data) => {
      const communicationUpdated = data.onUpdateCommunication as Communication;
      setCommunicationBodiesById((current) => mergeCommunicationBodies(current, [communicationUpdated]));
      if (!communicationUpdated._deleted) {
        dispatch({
          type: 'UPDATE_COMMUNICATION',
          payload: { communication: communicationUpdated },
        });
      }
    },
    (data) => {
      const { id } = data.onDeleteCommunication as Communication;
      dispatch({
        type: 'DELETE_COMMUNICATION',
        payload: { id },
      });
    }
  );

  useSubscriptions<
    OnCreateSignatureDocumentSubscription,
    OnUpdateSignatureDocumentSubscription,
    OnDeleteSignatureDocumentSubscription
  >(
    undefined,
    onUpdateSignatureDocument,
    undefined,
    undefined,
    (data) => {
      const updatedSignatureDocument = data.onUpdateSignatureDocument as SignatureDocument;
      dispatch({
        type: 'UPDATE_SIGNATURE_DOCUMENT_IN_COMMUNICATION',
        payload: { signatureDocument: updatedSignatureDocument },
      });
    },
    undefined
  );

  const entityLookups = useMemo<CommunicationEntityLookups>(
    () => ({
      leaseIds: new Set(leasesLoader.map((lease) => lease.id)),
      unitIds: new Set(unitsLoader.map((unit) => unit.id)),
      signatureDocumentsById: new Map(
        (signatureDocuments ?? []).map((signatureDocument) => [signatureDocument.id, signatureDocument])
      ),
    }),
    [leasesLoader, unitsLoader, signatureDocuments]
  );

  const { communicationsToSend, communicationsOutbox } = useMemo<{
    communicationsToSend: Communication[];
    communicationsOutbox: Communication[];
  }>(() => {
    if (!finishedLoading) {
      return { communicationsToSend: [], communicationsOutbox: [] };
    }
    const { communicationsToSend: filteredCommunicationsToSend, communicationsOutbox: filteredCommunicationsOutbox } =
      getFilteredAndClassifiedCommunicationsWithEntity(
        state.communicationsToSendOutboxRaw,
        entityLookups,
        signatureDocuments
      );

    return {
      communicationsToSend: filteredCommunicationsToSend,
      communicationsOutbox: filteredCommunicationsOutbox,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishedLoading, state.communicationsToSendOutboxRaw, entityLookups]);

  const { communicationsHistory } = useMemo<{
    communicationsHistory: Communication[];
  }>(() => {
    if (!finishedLoading) {
      return { communicationsHistory: [] };
    }

    const { communicationsHistory: filteredCommunicationsHistory } = getFilteredAndClassifiedCommunicationsWithEntity(
      state.communicationsHistoryRaw,
      entityLookups,
      signatureDocuments
    );

    return {
      communicationsHistory: filteredCommunicationsHistory,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishedLoading, state.communicationsHistoryRaw, entityLookups]);

  const { communicationsOutdated } = useMemo<{
    communicationsOutdated: Communication[];
  }>(() => {
    if (!finishedLoading) {
      return { communicationsOutdated: [] };
    }

    const { communicationsOutdated: filteredCommunicationsOutdated } = getFilteredAndClassifiedCommunicationsWithEntity(
      state.communicationsOutdatedRaw,
      entityLookups,
      signatureDocuments
    );

    return {
      communicationsOutdated: filteredCommunicationsOutdated,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishedLoading, state.communicationsOutdatedRaw, entityLookups]);

  const { communications } = useMemo<{
    communications: Communication[];
  }>(() => {
    if (!finishedLoading || isNilOrEmpty(userId)) {
      return { communications: [] };
    }

    return { communications: state.communicationsRaw };
  }, [finishedLoading, state.communicationsRaw, userId]);

  const requestLightCommunicationsForCategory = useCallback(
    (communicationCategory: CommunicationCategory, force: boolean = false) =>
      dispatch({ type: 'SHOULD_FETCH', payload: { communicationCategory, force } }),
    []
  );

  const setSyncCommunications = (force: boolean = false) => {
    dispatch({ type: 'SHOULD_SYNC_LIGHT', payload: { force } });
  };

  const createCommunication = async (input: Omit<Communication, 'id' | 'clientId' | 'readId'>) => {
    if (isNil(clientId)) {
      return;
    }
    try {
      return await createCommunicationRecord(input, clientId);
    } catch (error) {
      dispatch({ type: 'ERROR', payload: { error: error instanceof Error ? error.message : String(error) } });
    }
  };

  const updateCommunication = async (original: Communication | { id: string }, updates: Partial<Communication>) => {
    try {
      const communication = await fetchCommunication(original.id);
      const updatedCommunication = await mutation<Communication>(updateMutation, {
        input: {
          id: original.id,
          _version: communication._version,
          ...cleanInputUpdate(updates, false),
        },
      });
      return updatedCommunication;
    } catch (error) {
      dispatch({ type: 'ERROR', payload: { error: error instanceof Error ? error.message : String(error) } });
    }
  };

  const deleteCommunication = async (id: string) => {
    if (!communicationsDetailsDelete) {
      return;
    }
    return await mutation<MutationStatus, DeleteEntityByIdMutationVariables>(deleteEntityByIdMutation, {
      input: { entityId: id, entity: DeepDeletableEntityType.COMMUNICATION },
    });
  };

  const batchDeleteCommunications = async (communicationsIdsVersions: BatchChangeIdVersion[]) => {
    const result = await batchChangeCommunications(communicationsIdsVersions, BatchChangeCommunicationsAction.DELETE);
    if (result) {
      dispatch({
        type: 'DELETE_COMMUNICATIONS',
        payload: { ids: communicationsIdsVersions.map((communicationIdVersion) => communicationIdVersion.id) },
      });
    }
    return result;
  };

  const batchSendCommunications = async (communicationsIdsVersions: BatchChangeIdVersion[], resend = false) =>
    await batchChangeCommunications(
      communicationsIdsVersions,
      resend ? BatchChangeCommunicationsAction.RESEND : BatchChangeCommunicationsAction.SEND
    );

  const bulkUpdateCommunications = async (
    communicationsToUpdate: Communication[],
    updates: Partial<Communication>,
    dispatchUpdated: boolean = false
  ): Promise<number> => {
    const results = await processInChunks(
      communicationsToUpdate,
      async (communication) => {
        const updatedCommunication = await updateCommunication(communication, updates);
        if (dispatchUpdated && !isNil(updatedCommunication)) {
          dispatch({ type: 'UPDATE_COMMUNICATION', payload: { communication: updatedCommunication } });
        }
        return updatedCommunication;
      },
      COMMUNICATION_BULK_CONCURRENCY
    );
    return compact(results).length;
  };

  const sendOrScheduleCommunications = async (
    communicationsToSchedule: Communication[],
    scheduledAt: DateTime | null
  ) => {
    const sendNow = isNil(scheduledAt);
    return bulkUpdateCommunications(communicationsToSchedule, {
      status: sendNow ? CommunicationStatus.TO_SEND : CommunicationStatus.SCHEDULED,
      toSend: sendNow,
      scheduledAt: sendNow ? null : fromDateTime(scheduledAt),
    });
  };

  const ignoreCommunications = async (communicationsToIgnore: Communication[]) =>
    bulkUpdateCommunications(
      communicationsToIgnore,
      { status: CommunicationStatus.IGNORED, toSend: false, scheduledAt: null },
      true
    );

  const markCommunicationsAsSent = async (communicationsToMark: Communication[], sentAt: DateTime) =>
    bulkUpdateCommunications(communicationsToMark, {
      status: CommunicationStatus.SENT_MANUALLY,
      toSend: false,
      scheduledAt: null,
      sentAt: fromDateTime(sentAt),
    });

  const batchChangeCommunications = async (
    communicationsIdsVersions: BatchChangeIdVersion[],
    action: BatchChangeCommunicationsAction
  ) => {
    const result = await mutation<MutationStatus, BatchChangeCommunicationsMutationVariables>(
      batchChangeCommunicationsMutation,
      {
        input: { action, clientId: clientId!, userId: userId!, communications: communicationsIdsVersions },
      }
    );
    return result?.status ?? false;
  };

  const downloadCommunications = async (communicationIds: string[]) => {
    const result = await mutation<DownloadCommunicationsResult, DownloadCommunicationsMutationVariables>(
      downloadCommunicationsMutation,
      {
        input: {
          communicationIds,
          clientId: clientId!,
          userId: userId!,
          language,
        },
      }
    );
    return result;
  };

  const isDownloadingCommunication = (communicationId: string): boolean =>
    downloadingCommunicationIds.includes(communicationId);

  const withDownloadProgress = async (communicationIds: string[], download: () => Promise<boolean>) => {
    setDownloadingCommunicationIds((current) => [...current, ...communicationIds]);
    const progressKey = toast.info(formatMessage({ id: 'communications.download.started' }), { duration: Infinity });
    try {
      return await download();
    } catch (_error) {
      toast.error(formatMessage({ id: 'communications.download.error' }));
      return false;
    } finally {
      toast.dismiss(progressKey);
      setDownloadingCommunicationIds((current) => current.filter((currentId) => !communicationIds.includes(currentId)));
    }
  };

  const downloadCommunicationFiles = async (communicationsToDownload: Communication[]): Promise<boolean> => {
    const [firstCommunication] = communicationsToDownload;
    if (isNil(firstCommunication)) {
      return false;
    }
    const communicationIds = communicationsToDownload.map((communication) => communication.id);
    return withDownloadProgress(communicationIds, async () => {
      const result = await downloadCommunications(communicationIds);
      const fileNameBase = buildCommunicationDownloadFileNameBase(firstCommunication, formatMessage);
      return handleDownloadResult(result, fileNameBase, { formatMessage });
    });
  };

  const generateAndDownloadCommunication = async (communicationId: string): Promise<void> => {
    await withDownloadProgress([communicationId], async () => {
      const communication = await fetchCommunication(communicationId);
      const result = await downloadCommunications([communicationId]);
      const fileNameBase = buildCommunicationDownloadFileNameBase(communication, formatMessage);
      return handleDownloadResult(result, fileNameBase, { formatMessage });
    });
  };

  const submitCommunicationDialog = async (payload: Omit<SubmitCommunicationDialogInput, 'clientId'>) => {
    const result = await mutation<SubmitCommunicationDialogResult, SubmitCommunicationDialogMutationVariables>(
      submitCommunicationDialogMutation,
      {
        input: { ...payload, clientId: clientId! },
      }
    );
    return result;
  };

  const fetchCommunicationBodiesForSource = (source: CommunicationBodySource): Promise<Communication[]> => {
    const tableClientId = getTableClientId(clientId!, COMMUNICATION_TABLE_NAME);
    switch (source.kind) {
      case 'category':
        return fetchLightCommunications(
          FetchByType.BY_CLIENT,
          tableClientId,
          getCommunicationCategoryStatuses(source.category),
          syncCommunicationBodies
        );
      case 'lease':
        return fetchLightCommunications(FetchByType.BY_LEASE, source.leaseId, undefined, syncCommunicationBodies);
      case 'unit':
        return fetchLightCommunicationsByUnit(source.unitId, tableClientId, syncCommunicationBodies);
      case 'building':
        return fetchLightCommunicationsByBuilding(
          source.buildingId,
          getBuildingUnitIds(source.buildingId),
          tableClientId,
          syncCommunicationBodies
        );
      case 'owner':
      default: {
        const { ownerLeaseIds, ownerUnitIds, ownerBuildingIds } = getOwnerEntityIds(source.ownerUnits);
        return fetchLightCommunicationsForOwner(
          ownerLeaseIds,
          ownerUnitIds,
          ownerBuildingIds,
          source.ownerId,
          tableClientId,
          syncCommunicationBodies
        );
      }
    }
  };

  const loadCommunicationBodiesForSource = (source: CommunicationBodySource): Promise<string[]> => {
    const sourceKey = getCommunicationBodySourceKey(source);
    const pendingLoad = bodySourceLoads.current.get(sourceKey);
    if (!isNil(pendingLoad)) {
      return pendingLoad;
    }
    const load = fetchCommunicationBodiesForSource(source)
      .then((communicationsWithBody) => {
        setCommunicationBodiesById((current) => mergeCommunicationBodies(current, communicationsWithBody));
        return communicationsWithBody.map((communication) => communication.id);
      })
      .finally(() => bodySourceLoads.current.delete(sourceKey));
    bodySourceLoads.current.set(sourceKey, load);
    return load;
  };

  const loadCommunicationBodiesByIds = async (communicationIds: string[]) => {
    const missingIds = communicationIds.filter((communicationId) => !loadingBodyIds.current.has(communicationId));
    if (isEmpty(missingIds)) {
      return;
    }
    missingIds.forEach((communicationId) => loadingBodyIds.current.add(communicationId));
    try {
      const communicationsWithBody = await processInChunks(
        missingIds,
        (communicationId) => get<Communication>(getCommunicationBody, communicationId),
        COMMUNICATION_BULK_CONCURRENCY
      );
      setCommunicationBodiesById((current) =>
        mergeCommunicationBodies(current, withEmptyBodiesForMissing(missingIds, compact(communicationsWithBody)))
      );
    } finally {
      missingIds.forEach((communicationId) => loadingBodyIds.current.delete(communicationId));
    }
  };

  const ensureCommunicationBodies = async (missingIds: string[], source?: CommunicationBodySource) => {
    if (isEmpty(missingIds) || !canFetchCommunications) {
      return;
    }
    try {
      if (missingIds.length > MAX_COMMUNICATION_BODIES_FETCHED_BY_ID && !isNil(source)) {
        const loadedIdSet = new Set(await loadCommunicationBodiesForSource(source));
        await loadCommunicationBodiesByIds(missingIds.filter((missingId) => !loadedIdSet.has(missingId)));
        return;
      }
      await loadCommunicationBodiesByIds(missingIds);
    } catch (error) {
      console.error('[communications] fetching the communication bodies failed', error);
      setTimeout(() => setBodyFetchRetryCount((current) => current + 1), MILLISECONDS_BEFORE_RETRYING_A_FAILED_FETCH);
    }
  };

  const requestCommunicationsForUnit = useCallback(
    (unitId: string) => dispatch({ type: 'FETCH_UNIT', payload: { unitId } }),
    []
  );

  const getLoadedCommunicationsForUnit = (unitId: string): Communication[] => {
    if (state.communicationsLoading || state.communicationsError) {
      return [];
    }

    return communications.filter(
      (communication) => communication.unit?.id === unitId || communication.unitId === unitId
    );
  };

  const getCommunicationsForUnit = (unitId: string): Communication[] => {
    requestCommunicationsForUnit(unitId);
    return getLoadedCommunicationsForUnit(unitId);
  };

  const requestCommunicationsForLease = useCallback(
    (leaseId: string) => dispatch({ type: 'FETCH_LEASE', payload: { leaseId } }),
    []
  );

  const getLoadedCommunicationsForLease = (leaseId: string): Communication[] => {
    if (state.communicationsLoading || state.communicationsError) {
      return [];
    }

    return communications.filter((communication) => communication.leaseId === leaseId);
  };

  const hasLoadedCommunicationsForLease = (leaseId: string): boolean =>
    fetchedEntityKeys.current.has(`lease:${leaseId}`);

  const getCommunicationsForLease = (leaseId: string): Communication[] => {
    requestCommunicationsForLease(leaseId);
    return getLoadedCommunicationsForLease(leaseId);
  };

  const getCommunicationsForBuilding = (buildingId: string): Communication[] => {
    dispatch({ type: 'FETCH_BUILDING', payload: { buildingId } });

    if (state.communicationsLoading || state.communicationsError) {
      return [];
    }

    const unitIds = new Set(getBuildingUnitIds(buildingId));
    return communications.filter(
      (communication) =>
        communication.buildingId === buildingId ||
        (!isNil(communication.unitId) && unitIds.has(communication.unitId)) ||
        (!isNil(communication.unit?.id) && unitIds.has(communication.unit.id))
    );
  };

  const getLightCommunicationsForCategory = (communicationCategory: CommunicationCategory): Communication[] => {
    if (state.communicationsError) {
      return [];
    }
    switch (communicationCategory) {
      case CommunicationCategory.OUTBOX:
        return communicationsOutbox;
      case CommunicationCategory.HISTORY:
        return communicationsHistory;
      case CommunicationCategory.OUTDATED:
        return communicationsOutdated;
      case CommunicationCategory.TO_SEND:
      default:
        return communicationsToSend;
    }
  };

  const getLightCommunicationsForOwner = (ownerUnits: Unit[], ownerId: string): Communication[] => {
    const { ownerLeaseIds, ownerUnitIds, ownerBuildingIds } = getOwnerEntityIds(ownerUnits);

    dispatch({ type: 'FETCH_OWNER', payload: { ownerLeaseIds, ownerUnitIds, ownerBuildingIds, ownerId } });
    if (state.communicationsLoading || state.communicationsError) {
      return [];
    }

    return communications.filter(
      (communication) =>
        ownerLeaseIds.includes(communication.leaseId ?? '') ||
        ownerUnitIds.includes(communication.unitId ?? '') ||
        ownerBuildingIds.includes(communication.buildingId ?? '') ||
        (communication.contactIds ?? []).includes(ownerId) ||
        (communication.concernedContactIds ?? []).includes(ownerId)
    );
  };

  const requestCommunication = useCallback((id: string) => dispatch({ type: 'FETCH_CLASSIC', payload: { id } }), []);

  const getLoadedCommunication = (id: string): Communication | undefined => {
    if (state.communicationsLoading || state.communicationsError) {
      return undefined;
    }
    const communication = communications.find((currentCommunication) => currentCommunication.id === id);
    if (!isNilOrEmpty(communication?.body)) {
      return communication;
    }
    return undefined;
  };

  const getCommunication = (id: string): Communication | undefined => {
    requestCommunication(id);
    return getLoadedCommunication(id);
  };

  const communicationsToSendSettled = finishedLoading && (!isNil(state.lastSync) || !isNil(state.communicationsError));

  const values = useMemo(
    () => ({
      ...state,
      communicationsToSendSettled,
      communicationsToSend,
      communicationsOutbox,
      communicationsHistory,
      communicationsOutdated,
      createCommunication,
      updateCommunication,
      deleteCommunication,
      batchDeleteCommunications,
      batchSendCommunications,
      sendOrScheduleCommunications,
      ignoreCommunications,
      markCommunicationsAsSent,
      getCommunicationsForBuilding,
      getCommunicationsForUnit,
      getLoadedCommunicationsForUnit,
      requestCommunicationsForUnit,
      getCommunicationsForLease,
      getLoadedCommunicationsForLease,
      hasLoadedCommunicationsForLease,
      requestCommunicationsForLease,
      getCommunication,
      getLoadedCommunication,
      requestCommunication,
      communicationBodiesById,
      bodyFetchRetryCount,
      ensureCommunicationBodies,
      getLightCommunicationsForCategory,
      requestLightCommunicationsForCategory,
      getLightCommunicationsForOwner,
      setSyncCommunications,
      downloadCommunications,
      downloadCommunicationFiles,
      generateAndDownloadCommunication,
      isDownloadingCommunication,
      submitCommunicationDialog,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state,
      communicationsToSendSettled,
      communicationsToSend,
      communicationsOutbox,
      communicationsHistory,
      communicationsOutdated,
      communicationBodiesById,
      bodyFetchRetryCount,
      canFetchCommunications,
    ]
  );

  return <CommunicationContext.Provider value={values}>{children}</CommunicationContext.Provider>;
};

export const useCommunications = (): CommunicationContext => {
  const firstRender = useRef<boolean>(false);
  const context = useContext<CommunicationContext | null>(CommunicationContext);

  if (isNil(context)) {
    throw new Error('`useCommunications` hook must be used within a `CommunicationContextProvider` component');
  }
  useEffect(() => {
    if (!firstRender.current && isEmpty(context.communicationsToSendOutboxRaw) && isNil(context.lastSync)) {
      context.setSyncCommunications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRender]);

  return context as CommunicationContext;
};

export const useLightCommunicationsForCategory = (communicationCategory: CommunicationCategory): Communication[] => {
  const { getLightCommunicationsForCategory, requestLightCommunicationsForCategory } = useCommunications();

  useEffect(() => {
    requestLightCommunicationsForCategory(communicationCategory);
  }, [communicationCategory, requestLightCommunicationsForCategory]);

  return getLightCommunicationsForCategory(communicationCategory);
};

export const useLoadedCommunications = (
  communicationIds: (string | null | undefined)[]
): (Communication | undefined)[] => {
  const { getLoadedCommunication, requestCommunication } = useCommunications();

  // No dependency list on purpose: every render re-requests, as the render-time getters did, so
  // FETCH_CLASSIC refreshes a record once it is older than NUMBER_OF_MINUTES_FOR_REFETCH.
  useEffect(() => {
    compact(communicationIds).forEach((communicationId) => requestCommunication(communicationId));
  });

  return communicationIds.map((communicationId) =>
    isNilOrEmpty(communicationId) ? undefined : getLoadedCommunication(communicationId)
  );
};

export const useLoadedCommunication = (communicationId?: string | null): Communication | undefined =>
  useLoadedCommunications([communicationId])[0];
